import Image from 'next/image';
import Link from 'next/link';
import type { CSSProperties } from 'react';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  CheckGlyph,
  delay,
  SectionHeading,
  tone,
} from '@/components/agents/primitives';
import { useReveal } from '@/components/agents/useReveal';

import { PHOTOS, useAboutTranslation } from './kit';

type Credential = {
  /** Official names, kept in English. */
  name: string;
  badge?: string;
};

type Founder = {
  name: string;
  role: string;
  photo: string;
  /** Framing for a headshot that sits small in its square. */
  photoClassName?: string;
  bio: string;
  highlights: string[];
  credentials: Credential[];
  link?: { label: string; href: string };
};

export default function AboutFounders() {
  const { t } = useAboutTranslation();
  const { ref, state } = useReveal<HTMLDivElement>(0.15);

  // Names are people, so they are never translated.
  const founders: Founder[] = [
    {
      name: 'Marianna Olefyrenko',
      role: t('CEO and Co-Founder'),
      photo: PHOTOS.marianna,
      photoClassName: 'origin-[50%_8%] scale-[1.35]',
      bio: t(
        'Marianna runs the company and leads the product. She has worked in IT for more than 15 years, across AI, software engineering, data analytics and UX design. She is also a certified trainer who teaches business owners and teams how to put AI to work.',
      ),
      highlights: [
        t('15+ years in AI, software engineering, data analytics and UX'),
        t('Taught generative AI bootcamps for Vertical Institute'),
        t('Speaker at the NTUC Career Festival 2026'),
        t('Google Developer Group member since 2019'),
      ],
      credentials: [
        { name: 'WSQ Advanced Certificate in Learning and Performance' },
        { name: 'AWS Generative AI Essentials', badge: PHOTOS.awsGenAi },
        { name: 'NTU: AI and Analytics for Business Valuation and Finance' },
      ],
      link: {
        label: t('Book a workshop with Marianna'),
        href: '/ai-workshops',
      },
    },
    {
      name: 'Dmytro Olefyrenko',
      role: t('CTO and Co-Founder'),
      photo: PHOTOS.dmytro,
      bio: t(
        'Dmytro built the first version of Easy-Peasy.AI and leads engineering, from Marky Agent to the AI agents businesses use with their customers. He has 16+ years in software engineering. Earlier, he was CTO of Kickoff by Zuju and led frontend teams at BeMyGuest in Singapore.',
      ),
      highlights: [
        t('16+ years in software engineering'),
        t('Open-source Vue libraries with 1.8M+ npm downloads a year'),
        t('Speaker at Google I/O Extended 2019 in Singapore'),
        t('Invited by Google to Google I/O in Mountain View'),
        t('Google Developer Group member since 2018'),
      ],
      credentials: [
        {
          name: 'AWS Certified Solutions Architect – Associate',
          badge: PHOTOS.awsArchitect,
        },
        { name: 'AWS Certified Developer – Associate' },
        { name: 'Google Mobile Web Specialist (2018)' },
      ],
    },
  ];

  return (
    <section id='founders' className='scroll-mt-8 py-20 sm:py-28'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <SectionHeading
          title={t('Meet the founders.')}
          tail={t('Still building, still teaching.')}
          lead={t(
            'Easy-Peasy.AI is founder-led. Marianna runs the company and the product, and Dmytro leads engineering.',
          )}
        />

        <div
          ref={ref}
          data-reveal={state}
          className={clsxm(styles.reveal, 'mt-14 grid gap-5 lg:grid-cols-2')}
        >
          {founders.map((founder, index) => (
            <FounderCard
              key={founder.name}
              founder={founder}
              style={delay(index * 120)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function FounderCard({
  founder,
  style,
}: {
  founder: Founder;
  style: CSSProperties;
}) {
  const { t } = useAboutTranslation();
  const {
    name,
    role,
    photo,
    photoClassName,
    bio,
    highlights,
    credentials,
    link,
  } = founder;

  const linkClass =
    'inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 underline-offset-4 hover:underline dark:text-primary-400';

  return (
    <article
      className={clsxm(
        styles.rv,
        'flex flex-col rounded-[28px] p-7 sm:p-9',
        tone.surface,
        tone.line,
      )}
      style={style}
    >
      <div className='flex items-center gap-5'>
        <span className='relative h-20 w-20 flex-none overflow-hidden rounded-[22px] bg-[#e8e8f0] ring-1 ring-[#0f0f28]/[0.07] sm:h-24 sm:w-24 dark:bg-white/5 dark:ring-white/10'>
          <Image
            src={photo}
            alt={name}
            fill
            unoptimized
            className={clsxm('object-cover', photoClassName)}
          />
        </span>
        <div className='min-w-0'>
          <h3
            className={clsxm(
              'text-2xl font-semibold tracking-[-0.02em] sm:text-[1.75rem]',
              tone.ink,
            )}
          >
            {name}
          </h3>
          <p className='mt-1 text-[15px] font-medium text-primary-600 dark:text-primary-400'>
            {role}
          </p>
        </div>
      </div>

      <p className={clsxm('mt-7 text-[16px] leading-7', tone.body)}>{bio}</p>

      <ul className='mt-6 space-y-3'>
        {highlights.map((highlight) => (
          <li
            key={highlight}
            className={clsxm('flex gap-3 text-[15px] leading-6', tone.body)}
          >
            <span className='mt-1 flex h-4 w-4 flex-none items-center justify-center rounded-full bg-primary-600 text-white'>
              <CheckGlyph className='h-2.5 w-2.5' />
            </span>
            {highlight}
          </li>
        ))}
      </ul>

      <div className='mt-auto pt-8'>
        <div className='border-t border-[#0f0f28]/[0.08] pt-6 dark:border-white/[0.08]'>
          <p className={clsxm('text-[13px] font-medium', tone.quiet)}>
            {t('Certificates')}
          </p>
          <ul className='mt-3 flex flex-wrap gap-2'>
            {credentials.map(({ name: credential, badge }) => (
              <li
                key={credential}
                className={clsxm(
                  'inline-flex items-center gap-2 rounded-full py-1 pe-3 text-[13px] font-medium',
                  badge ? 'ps-1' : 'ps-3',
                  'bg-[#f3f3f8] dark:bg-white/[0.06]',
                  tone.ink,
                )}
              >
                {badge ? (
                  <Image
                    src={badge}
                    alt=''
                    width={24}
                    height={24}
                    unoptimized
                    className='h-6 w-6 flex-none object-contain'
                  />
                ) : null}
                <span dir='ltr'>{credential}</span>
              </li>
            ))}
          </ul>
          {link ? (
            <div className='mt-6'>
              <Link href={link.href} className={linkClass}>
                {link.label}
                <ArrowGlyph className='h-3.5 w-3.5' />
              </Link>
            </div>
          ) : null}
        </div>
      </div>
    </article>
  );
}
